import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Moon, Sun, Bell, Lock, Globe, Palette, 
  Save, Mail, Shield, Smartphone,
  FileSignature, HelpCircle, RotateCcw, Loader2,
  Filter, Reply, Forward, Ban, ArrowLeft, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { OTPInput } from '@/components/ui/otp-input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import SignaturesSection from '../components/settings/SignaturesSection';
import { FiltersSection, AutoReplySection, ForwardingSection, BlockedSection } from '../components/settings/MailRules';

export const SettingsPage = ({ activeSection: externalActiveSection, onSectionChange, onBack }) => {
  const { theme, toggleTheme } = useTheme();
  const [internalActiveSection, setInternalActiveSection] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  
  // Use external state if provided, otherwise internal
  const activeSection = externalActiveSection || internalActiveSection;
  const setActiveSection = (section) => {
    if (onSectionChange) {
      onSectionChange(section);
    } else {
      setInternalActiveSection(section);
    }
  };
  
  const [settings, setSettings] = useState({
    notifications: true,
    emailNotifications: true,
    soundEnabled: true,
    language: 'tr',
    timezone: 'Europe/Istanbul'
  });

  // Profile state
  const [profileName, setProfileName] = useState('');
  const [profileAvatar, setProfileAvatar] = useState('');
  const profileFileInputRef = useRef(null);

  // 2FA State
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState(null); // 'sms' or 'authenticator'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [is2FAStep2, setIs2FAStep2] = useState(false);
  const [is2FALoading, setIs2FALoading] = useState(false);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrCode, setTotpQrCode] = useState('');
  const [totpUri, setTotpUri] = useState('');

  // Signature settings

  // IP Restriction state
  const [showIPDialog, setShowIPDialog] = useState(false);
  const [newIPAddress, setNewIPAddress] = useState('');
  const [isIPLoading, setIsIPLoading] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load settings from API
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await api.getSettings();
      setUserSettings(data);
      setSettings(prev => ({
        ...prev,
        language: data.language || 'tr'
      }));
      // Set profile data
      setProfileName(data.display_name || '');
      setProfileAvatar(data.avatar_url || '');
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async (updates) => {
    try {
      setIsSaving(true);
      await api.updateSettings(updates);
      await loadSettings();
      toast.success('Ayarlar kaydedildi');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.message || 'Ayarlar kaydedilemedi');
    } finally {
      setIsSaving(false);
    }
  };

  // Save profile (display name + avatar)
  const saveProfile = async () => {
    try {
      setIsSaving(true);
      await api.updateProfile(profileName, profileAvatar);
      await loadSettings();
      toast.success('Profil güncellendi');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error(error.message || 'Profil kaydedilemedi');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle profile photo upload
  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Dosya boyutu 2MB\'dan küçük olmalıdır');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır');
      return;
    }
    try {
      setIsSaving(true);
      await api.changePassword(currentPassword, newPassword);
      toast.success('Şifre başarıyla değiştirildi');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message || 'Şifre değiştirilemedi');
    } finally {
      setIsSaving(false);
    }
  };

  // 2FA SMS Setup or Phone Change
  const handleSetup2FA = async () => {
    if (twoFAMethod === 'sms') {
      // Validate phone number (10 digits, not starting with 0)
      const cleanPhone = phoneNumber.replace(/\s/g, '');
      if (cleanPhone.length !== 10 || cleanPhone.startsWith('0')) {
        toast.error('Geçersiz telefon numarası. 10 haneli olmalı ve 0 ile başlamamalı.');
        return;
      }
      
      try {
        setIs2FALoading(true);
        
        // If 2FA is already enabled, this is a phone change
        if (userSettings?.two_fa_enabled) {
          await api.updatePhoneNumber(cleanPhone);
        } else {
          await api.setup2FA('sms', cleanPhone);
        }
        
        setIs2FAStep2(true);
        toast.success('Doğrulama kodu gönderildi', {
          description: `+90${cleanPhone} numarasına SMS gönderildi`,
        });
      } catch (error) {
        toast.error(error.message || 'SMS gönderilemedi');
      } finally {
        setIs2FALoading(false);
      }
    } else if (twoFAMethod === 'authenticator') {
      try {
        setIs2FALoading(true);
        const response = await api.setup2FA('authenticator');
        setTotpSecret(response.secret);
        setTotpQrCode(response.qr_code);
        setTotpUri(response.uri);
        setIs2FAStep2(true);
      } catch (error) {
        toast.error(error.message || 'Authenticator kurulumu başlatılamadı');
      } finally {
        setIs2FALoading(false);
      }
    }
  };

  const handleVerify2FA = async () => {
    if (smsCode.length !== 6) {
      toast.error('6 haneli doğrulama kodunu girin');
      return;
    }
    
    try {
      setIs2FALoading(true);
      
      // If 2FA is already enabled, this is a phone change verification
      if (userSettings?.two_fa_enabled && twoFAMethod === 'sms') {
        await api.verifyPhoneChange(smsCode);
        toast.success('Telefon numarası güncellendi');
      } else {
        await api.verify2FASetup(smsCode);
        const methodName = twoFAMethod === 'sms' ? 'SMS' : 'Authenticator';
        toast.success('2FA başarıyla etkinleştirildi', {
          description: `Artık girişlerde ${methodName} doğrulaması gerekecek`,
        });
      }
      
      await loadSettings();
      reset2FADialog();
    } catch (error) {
      toast.error(error.message || 'Doğrulama başarısız');
      setSmsCode(''); // Clear code on error
    } finally {
      setIs2FALoading(false);
    }
  };

  const reset2FADialog = () => {
    setShow2FADialog(false);
    setTwoFAMethod(null);
    setPhoneNumber('');
    setSmsCode('');
    setIs2FAStep2(false);
    setTotpSecret('');
    setTotpQrCode('');
    setTotpUri('');
  };

  const handleDisable2FA = async () => {
    if (!confirm('2FA\'yı devre dışı bırakmak istediğinize emin misiniz?')) {
      return;
    }
    try {
      await api.disable2FA();
      await loadSettings();
      toast.success('2FA devre dışı bırakıldı');
    } catch (error) {
      toast.error(error.message || '2FA devre dışı bırakılamadı');
    }
  };

  // IP Restriction functions
  const handleEnableIPRestriction = async () => {
    try {
      setIsIPLoading(true);
      await api.enableIPRestriction();
      await loadSettings();
      toast.success('IP kısıtlaması etkinleştirildi', {
        description: 'Mevcut IP adresiniz otomatik olarak eklendi',
      });
    } catch (error) {
      toast.error(error.message || 'IP kısıtlaması etkinleştirilemedi');
    } finally {
      setIsIPLoading(false);
    }
  };

  const handleDisableIPRestriction = async () => {
    if (!confirm('IP kısıtlamasını devre dışı bırakmak istediğinize emin misiniz? Tüm IP\'lerden erişim serbest olacak.')) {
      return;
    }
    try {
      setIsIPLoading(true);
      await api.disableIPRestriction();
      await loadSettings();
      toast.success('IP kısıtlaması devre dışı bırakıldı');
    } catch (error) {
      toast.error(error.message || 'IP kısıtlaması devre dışı bırakılamadı');
    } finally {
      setIsIPLoading(false);
    }
  };

  const handleAddIP = async () => {
    if (!newIPAddress.trim()) {
      toast.error('IP adresi girin');
      return;
    }
    
    // Basic IP validation
    const ipv4Pattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipv4Pattern.test(newIPAddress.trim())) {
      toast.error('Geçersiz IP adresi formatı');
      return;
    }
    
    try {
      setIsIPLoading(true);
      await api.addAllowedIP(newIPAddress.trim());
      await loadSettings();
      setNewIPAddress('');
      setShowIPDialog(false);
      toast.success('IP adresi eklendi');
    } catch (error) {
      toast.error(error.message || 'IP adresi eklenemedi');
    } finally {
      setIsIPLoading(false);
    }
  };

  const handleRemoveIP = async (ip) => {
    if (!confirm(`${ip} adresini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      await api.removeAllowedIP(ip);
      await loadSettings();
      toast.success('IP adresi kaldırıldı');
    } catch (error) {
      toast.error(error.message || 'IP adresi kaldırılamadı');
    }
  };

  // Menü: gruplu bölümler (cPanel'e bağlı olanlar yapılandırılmamışsa sayfada açıklama gösterir)
  const NAV_GROUPS = [
    {
      label: 'Hesap',
      items: [
        { id: 'profile', icon: User, label: 'Profil' },
        { id: 'security', icon: Lock, label: 'Güvenlik' },
        { id: 'notifications', icon: Bell, label: 'Bildirimler' },
      ],
    },
    {
      label: 'E-posta',
      items: [
        { id: 'signature', icon: FileSignature, label: 'İmzalar' },
        { id: 'filters', icon: Filter, label: 'Filtreler ve kurallar' },
        { id: 'autoreply', icon: Reply, label: 'Otomatik yanıt' },
        { id: 'forwarding', icon: Forward, label: 'Yönlendirme' },
        { id: 'blocked', icon: Ban, label: 'Engellenen adresler' },
      ],
    },
    {
      label: 'Tercihler',
      items: [
        { id: 'appearance', icon: Palette, label: 'Görünüm' },
        { id: 'language', icon: Globe, label: 'Dil ve bölge' },
        { id: 'help', icon: HelpCircle, label: 'Yardım' },
      ],
    },
  ];
  const WIDE_SECTIONS = ['signature', 'filters'];
  const currentGroup = NAV_GROUPS.find((g) => g.items.some((i) => i.id === activeSection));
  const currentItem = currentGroup?.items.find((i) => i.id === activeSection);

  const [navSearch, setNavSearch] = useState('');
  const [mobileContent, setMobileContent] = useState(Boolean(externalActiveSection && externalActiveSection !== 'profile'));
  const normalizedSearch = navSearch.trim().toLocaleLowerCase('tr-TR');
  const visibleGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => !normalizedSearch || i.label.toLocaleLowerCase('tr-TR').includes(normalizedSearch)) }))
    .filter((g) => g.items.length > 0);

  // Reset tour function
  const resetTour = () => {
    localStorage.removeItem('netlen-tour-completed');
    window.location.reload();
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-lg font-semibold mb-4">Profil Bilgileri</h3>
              <div className="flex items-center gap-6 mb-6">
                <Avatar className="h-20 w-20">
                  {profileAvatar ? (
                    <img src={profileAvatar} alt="Avatar" className="h-full w-full object-cover" />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 text-blue-700 dark:text-blue-200 text-xl font-semibold">
                      {(profileName || userSettings?.email || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <input
                    type="file"
                    ref={profileFileInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfilePhotoUpload}
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-lg"
                    onClick={() => profileFileInputRef.current?.click()}
                  >
                    Fotoğraf Değiştir
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">JPG, PNG veya GIF. Maks 2MB.</p>
                </div>
              </div>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="fullname">Ad Soyad</Label>
                  <Input 
                    id="fullname" 
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    className="max-w-md"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email"
                    defaultValue={userSettings?.email || ''}
                    className="max-w-md"
                    disabled
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-muted-foreground">Email adresi değiştirilemez.</p>
                </div>
                <Button 
                  onClick={saveProfile}
                  disabled={isSaving}
                  className="w-fit rounded-lg"
                  data-testid="save-profile-button"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Profili Kaydet
                </Button>
              </div>
            </div>
          </motion.div>
        );

      case 'appearance':
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold">Görünüm Ayarları</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl max-w-md">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                  <div>
                    <p className="font-medium">Tema</p>
                    <p className="text-sm text-muted-foreground">
                      {theme === 'dark' ? 'Koyu tema aktif' : 'Açık tema aktif'}
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  data-testid="theme-switch"
                />
              </div>

              <div className="p-4 bg-muted/30 rounded-xl max-w-md">
                <p className="font-medium mb-3">Tema Önizleme</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => theme === 'dark' && toggleTheme()}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === 'light' ? 'border-blue-500 bg-white' : 'border-transparent bg-white'
                    }`}
                    data-testid="light-theme-preview"
                  >
                    <div className="h-8 w-full bg-gray-100 rounded mb-2" />
                    <div className="h-2 w-3/4 bg-gray-200 rounded mb-1" />
                    <div className="h-2 w-1/2 bg-gray-200 rounded" />
                    <p className="text-xs text-gray-600 mt-2">Açık</p>
                  </button>
                  <button
                    onClick={() => theme === 'light' && toggleTheme()}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === 'dark' ? 'border-blue-500 bg-slate-900' : 'border-transparent bg-slate-900'
                    }`}
                    data-testid="dark-theme-preview"
                  >
                    <div className="h-8 w-full bg-slate-800 rounded mb-2" />
                    <div className="h-2 w-3/4 bg-slate-700 rounded mb-1" />
                    <div className="h-2 w-1/2 bg-slate-700 rounded" />
                    <p className="text-xs text-slate-400 mt-2">Koyu</p>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'notifications':
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold">Bildirim Ayarları</h3>
            
            <div className="space-y-4 max-w-md">
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Masaüstü Bildirimleri</p>
                    <p className="text-sm text-muted-foreground">Yeni mail geldiğinde bildirim al</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.notifications}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, notifications: checked }))}
                  data-testid="desktop-notifications-switch"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Email Bildirimleri</p>
                    <p className="text-sm text-muted-foreground">Önemli güncellemeler için email al</p>
                  </div>
                </div>
                <Switch 
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, emailNotifications: checked }))}
                  data-testid="email-notifications-switch"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Ses Efektleri</p>
                    <p className="text-sm text-muted-foreground">Bildirim sesi çal</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const audio = new Audio('/sounds/mail-notification.mp3');
                      audio.volume = 0.5;
                      audio.play().then(() => {
                        toast.success('Bildirim sesi çalıyor');
                      }).catch(err => {
                        toast.error('Ses çalınamadı: ' + err.message);
                      });
                    }}
                    data-testid="test-sound-button"
                  >
                    Test
                  </Button>
                  <Switch 
                    checked={settings.soundEnabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, soundEnabled: checked }))}
                    data-testid="sound-switch"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'security':
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold">Güvenlik</h3>
            
            <div className="space-y-4 max-w-md">
              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Şifre Değiştir</p>
                    <p className="text-sm text-muted-foreground">Hesap güvenliğiniz için şifrenizi güncelleyin</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <Input 
                    type="password" 
                    placeholder="Mevcut şifre" 
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    data-testid="current-password" 
                  />
                  <Input 
                    type="password" 
                    placeholder="Yeni şifre" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password" 
                  />
                  <Input 
                    type="password" 
                    placeholder="Yeni şifre (tekrar)" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password" 
                  />
                  <Button 
                    className="rounded-lg" 
                    onClick={handleChangePassword}
                    disabled={isSaving}
                    data-testid="change-password-button"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Şifreyi Güncelle
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5" />
                    <div>
                      <p className="font-medium">İki Faktörlü Doğrulama</p>
                      <p className="text-sm text-muted-foreground">
                        {userSettings?.two_fa_enabled 
                          ? (userSettings?.two_fa_method === 'sms' 
                              ? `SMS - ${userSettings?.phone_number || ''}` 
                              : 'Authenticator Uygulaması')
                          : 'Ekstra güvenlik katmanı ekleyin'}
                      </p>
                    </div>
                  </div>
                  {userSettings?.two_fa_enabled ? (
                    <div className="flex gap-2">
                      {userSettings?.two_fa_method === 'sms' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="rounded-lg"
                          onClick={() => {
                            setTwoFAMethod('sms');
                            setPhoneNumber('');
                            setIs2FAStep2(false);
                            setShow2FADialog(true);
                          }}
                        >
                          <Smartphone className="h-4 w-4 mr-1" />
                          Numara Değiştir
                        </Button>
                      )}
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        className="rounded-lg"
                        onClick={handleDisable2FA}
                        data-testid="disable-2fa-button"
                      >
                        Devre Dışı Bırak
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-lg"
                      onClick={() => setShow2FADialog(true)}
                      data-testid="enable-2fa-button"
                    >
                      Etkinleştir
                    </Button>
                  )}
                </div>
              </div>

              {/* IP Restriction Section */}
              <Separator />
              
              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5" />
                    <div>
                      <p className="font-medium">IP Kısıtlaması</p>
                      <p className="text-sm text-muted-foreground">
                        {userSettings?.ip_restriction_enabled 
                          ? `Aktif - ${userSettings?.allowed_ips?.length || 0} IP izinli` 
                          : 'Sadece belirli IP adreslerinden erişime izin ver'}
                      </p>
                    </div>
                  </div>
                  {userSettings?.ip_restriction_enabled ? (
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="rounded-lg"
                      onClick={handleDisableIPRestriction}
                      disabled={isIPLoading}
                      data-testid="disable-ip-restriction-button"
                    >
                      {isIPLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Devre Dışı Bırak'}
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-lg"
                      onClick={handleEnableIPRestriction}
                      disabled={isIPLoading}
                      data-testid="enable-ip-restriction-button"
                    >
                      {isIPLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Etkinleştir'}
                    </Button>
                  )}
                </div>
                
                {userSettings?.ip_restriction_enabled && (
                  <div className="space-y-3 mt-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">İzin Verilen IP Adresleri</Label>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowIPDialog(true)}
                        data-testid="add-ip-button"
                      >
                        + IP Ekle
                      </Button>
                    </div>
                    
                    {userSettings?.allowed_ips?.length > 0 ? (
                      <div className="space-y-2">
                        {userSettings.allowed_ips.map((ip, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 bg-background rounded-lg border"
                          >
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              <code className="text-sm font-mono">{ip}</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleRemoveIP(ip)}
                              data-testid={`remove-ip-${index}`}
                            >
                              Kaldır
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Henüz IP adresi eklenmemiş
                      </p>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      ⚠️ Dikkat: Kendi IP adresinizi silmemeye özen gösterin, aksi halde hesabınıza erişemezsiniz.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );

      case 'language':
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold">Dil & Bölge</h3>
            
            <div className="space-y-4 max-w-md">
              <div className="grid gap-2">
                <Label>Dil</Label>
                <Select value={settings.language} onValueChange={(val) => setSettings(prev => ({ ...prev, language: val }))}>
                  <SelectTrigger className="w-full" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tr">Türkçe</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Saat Dilimi</Label>
                <Select value={settings.timezone} onValueChange={(val) => setSettings(prev => ({ ...prev, timezone: val }))}>
                  <SelectTrigger className="w-full" data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Europe/Istanbul">İstanbul (UTC+3)</SelectItem>
                    <SelectItem value="Europe/London">Londra (UTC+0)</SelectItem>
                    <SelectItem value="America/New_York">New York (UTC-5)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>
        );

      case 'help':
        return (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold">Yardım</h3>
            
            <div className="space-y-4 max-w-md">
              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <RotateCcw className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Tanıtım Turunu Yeniden Başlat</p>
                    <p className="text-sm text-muted-foreground">
                      Uygulamanın temel özelliklerini gösteren interaktif turu tekrar görüntüleyin
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={resetTour} 
                  variant="outline" 
                  className="rounded-lg"
                  data-testid="reset-tour-button"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Turu Yeniden Başlat
                </Button>
              </div>

              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Destek</p>
                    <p className="text-sm text-muted-foreground">
                      Sorularınız için: destek@netlen.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'signature':
        return userSettings ? <SignaturesSection user={userSettings} /> : null;

      case 'filters':
        return <FiltersSection />;

      case 'autoreply':
        return <AutoReplySection />;

      case 'forwarding':
        return <ForwardingSection />;

      case 'blocked':
        return <BlockedSection />;

      default:
        return null;
    }
  };

  return (
    <div className="flex-1 flex h-full" data-testid="settings-page">
      {/* Ayarlar menüsü */}
      <aside
        className={`${mobileContent ? 'hidden' : 'flex'} md:flex w-full md:w-[280px] shrink-0 flex-col border-r border-border bg-panel`}
        data-testid="settings-nav"
      >
        <div className="p-4 space-y-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
            data-testid="settings-back"
          >
            <ArrowLeft className="h-4 w-4" />
            E-postaya dön
          </button>
          <h1 className="text-2xl font-semibold">Ayarlar</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder="Ayarlarda ara..."
              aria-label="Ayarlarda ara"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring/30"
              data-testid="settings-search"
            />
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-5">
          {visibleGroups.length === 0 && <p className="px-3 text-sm text-muted-foreground">Sonuç bulunamadı</p>}
          {visibleGroups.map((group) => (
            <div key={group.label}>
              <p className="px-3 mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.label}</p>
              <div className="space-y-0.5">
                {group.items.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => { setActiveSection(section.id); setMobileContent(true); }}
                    aria-current={activeSection === section.id ? 'page' : undefined}
                    className={`w-full flex items-center gap-3 px-3 h-10 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-foreground/80 hover:bg-accent/60'
                    }`}
                    data-testid={`settings-${section.id}`}
                  >
                    <section.icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left truncate">{section.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Ayarlar içeriği */}
      <div className={`${mobileContent ? 'block' : 'hidden'} md:block flex-1 min-w-0 bg-background overflow-y-auto`}>
        <div className={`p-4 sm:p-8 ${WIDE_SECTIONS.includes(activeSection) ? 'max-w-6xl' : 'max-w-3xl'}`}>
          <button
            onClick={() => setMobileContent(false)}
            className="md:hidden flex items-center gap-2 text-sm text-primary mb-4"
            data-testid="settings-list-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Ayarlar
          </button>
          {currentItem && (
            <nav className="text-sm text-muted-foreground mb-4" aria-label="Konum">
              Ayarlar <span className="mx-1">/</span> {currentGroup.label} <span className="mx-1">/</span> {currentItem.label}
            </nav>
          )}
          {renderSection()}
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FADialog} onOpenChange={(open) => {
        if (!open) {
          reset2FADialog();
        }
        setShow2FADialog(open);
      }}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-dialog">
          <DialogHeader>
            <DialogTitle>
              {userSettings?.two_fa_enabled 
                ? (userSettings?.two_fa_method === 'sms' ? 'Telefon Numarası Değiştir' : '2FA Ayarları')
                : 'İki Faktörlü Doğrulama'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Step 1: Method Selection (only for new setup) */}
            {!twoFAMethod && !userSettings?.two_fa_enabled && (
              <>
                <p className="text-sm text-muted-foreground">
                  Hesabınızı korumak için bir doğrulama yöntemi seçin.
                </p>
                <div className="grid gap-3">
                  <button
                    onClick={async () => {
                      setTwoFAMethod('authenticator');
                      // Otomatik QR oluştur
                      try {
                        setIs2FALoading(true);
                        const response = await api.setup2FA('authenticator');
                        setTotpSecret(response.secret);
                        setTotpQrCode(response.qr_code);
                        setTotpUri(response.uri);
                        setIs2FAStep2(true);
                      } catch (error) {
                        toast.error(error.message || 'Authenticator kurulumu başlatılamadı');
                        setTwoFAMethod(null);
                      } finally {
                        setIs2FALoading(false);
                      }
                    }}
                    disabled={is2FALoading}
                    className="flex items-center gap-4 p-4 rounded-xl border-2 border-border hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all text-left disabled:opacity-50"
                    data-testid="2fa-method-authenticator"
                  >
                    <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                      {is2FALoading && twoFAMethod === 'authenticator' ? (
                        <Loader2 className="h-6 w-6 text-purple-600 dark:text-purple-400 animate-spin" />
                      ) : (
                        <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Authenticator Uygulaması</p>
                      <p className="text-sm text-muted-foreground">Google Authenticator, Microsoft Authenticator vb.</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setTwoFAMethod('sms')}
                    disabled={is2FALoading}
                    className="flex items-center gap-4 p-4 rounded-xl border-2 border-border hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all text-left disabled:opacity-50"
                    data-testid="2fa-method-sms"
                  >
                    <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                      <Smartphone className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">SMS Doğrulama</p>
                      <p className="text-sm text-muted-foreground">Cep telefonunuza kod gönderilir</p>
                    </div>
                  </button>
                </div>
              </>
            )}

            {/* Step 2a: SMS Setup */}
            {(twoFAMethod === 'sms' || (userSettings?.two_fa_enabled && userSettings?.two_fa_method === 'sms')) && !is2FAStep2 && (
              <>
                <p className="text-sm text-muted-foreground">
                  {userSettings?.two_fa_enabled 
                    ? 'Yeni cep telefonu numaranızı girin.'
                    : 'Cep telefonu numaranızı girin. Size bir doğrulama kodu göndereceğiz.'
                  }
                </p>
                <div className="space-y-2">
                  <Label htmlFor="phone">Cep Telefonu</Label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 bg-muted rounded-lg border text-sm text-muted-foreground">
                      +90
                    </div>
                    <Input
                      id="phone"
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhoneNumber(val);
                      }}
                      placeholder="5XX XXX XXXX"
                      className="flex-1"
                      maxLength={10}
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">10 haneli telefon numaranızı girin (0 ile başlamadan)</p>
                </div>
              </>
            )}

            {/* Step 3a: SMS Code Entry */}
            {twoFAMethod === 'sms' && is2FAStep2 && (
              <>
                <p className="text-sm text-muted-foreground">
                  +90{phoneNumber} numarasına gönderilen 6 haneli doğrulama kodunu girin.
                </p>
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-center block">Doğrulama Kodu</Label>
                  <OTPInput
                    length={6}
                    value={smsCode}
                    onChange={setSmsCode}
                    onComplete={async (code) => {
                      if (code.length !== 6) return;
                      setSmsCode(code);
                      try {
                        setIs2FALoading(true);
                        if (userSettings?.two_fa_enabled) {
                          await api.verifyPhoneChange(code);
                          toast.success('Telefon numarası güncellendi');
                        } else {
                          await api.verify2FASetup(code);
                          toast.success('2FA başarıyla etkinleştirildi', {
                            description: 'Artık girişlerde SMS doğrulaması gerekecek',
                          });
                        }
                        await loadSettings();
                        reset2FADialog();
                      } catch (error) {
                        toast.error(error.message || 'Doğrulama başarısız');
                        setSmsCode('');
                      } finally {
                        setIs2FALoading(false);
                      }
                    }}
                    disabled={is2FALoading}
                  />
                </div>
              </>
            )}

            {/* Step 3b: Authenticator QR & Code Entry */}
            {twoFAMethod === 'authenticator' && is2FAStep2 && (
              <>
                <div className="text-center space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Authenticator uygulamanızla QR kodu tarayın veya kodu manuel girin.
                  </p>
                  
                  {totpQrCode && (
                    <div className="flex justify-center">
                      <div className="p-4 bg-white rounded-xl border shadow-sm">
                        <img 
                          src={`data:image/png;base64,${totpQrCode}`} 
                          alt="QR Code" 
                          className="w-48 h-48"
                          data-testid="totp-qr-code"
                        />
                      </div>
                    </div>
                  )}
                  
                  {totpSecret && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Manuel Giriş Kodu:</p>
                      <code className="text-sm font-mono font-bold select-all" data-testid="totp-secret">
                        {totpSecret}
                      </code>
                    </div>
                  )}
                  
                  <div className="space-y-3 pt-2">
                    <Label className="text-sm font-medium text-center block">Doğrulama Kodu</Label>
                    <OTPInput
                      length={6}
                      value={smsCode}
                      onChange={setSmsCode}
                      onComplete={async (code) => {
                        if (code.length !== 6) return;
                        setSmsCode(code);
                        // Directly call API with the code
                        try {
                          setIs2FALoading(true);
                          await api.verify2FASetup(code);
                          toast.success('2FA başarıyla etkinleştirildi', {
                            description: 'Artık girişlerde Authenticator doğrulaması gerekecek',
                          });
                          await loadSettings();
                          reset2FADialog();
                        } catch (error) {
                          toast.error(error.message || 'Doğrulama başarısız');
                          setSmsCode('');
                        } finally {
                          setIs2FALoading(false);
                        }
                      }}
                      disabled={is2FALoading}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={reset2FADialog}
            >
              İptal
            </Button>
            
            {/* Back button for method selection */}
            {twoFAMethod && !is2FAStep2 && !userSettings?.two_fa_enabled && (
              <Button 
                variant="ghost"
                onClick={() => setTwoFAMethod(null)}
              >
                Geri
              </Button>
            )}
            
            {/* SMS: Send Code button */}
            {twoFAMethod === 'sms' && !is2FAStep2 && (
              <Button 
                onClick={handleSetup2FA}
                disabled={is2FALoading || phoneNumber.length !== 10}
                data-testid="send-sms-button"
              >
                {is2FALoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Kod Gönder
              </Button>
            )}
            
            {/* Verify button (both methods) */}
            {is2FAStep2 && (
              <Button 
                onClick={handleVerify2FA}
                disabled={is2FALoading || smsCode.length !== 6}
                data-testid="verify-2fa-button"
              >
                {is2FALoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Doğrula ve Etkinleştir
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IP Add Dialog */}
      <Dialog open={showIPDialog} onOpenChange={setShowIPDialog}>
        <DialogContent className="sm:max-w-md" data-testid="add-ip-dialog">
          <DialogHeader>
            <DialogTitle>IP Adresi Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Erişime izin vermek istediğiniz IP adresini girin. Sadece IPv4 formatı desteklenmektedir.
            </p>
            <div className="space-y-2">
              <Label htmlFor="ip-address">IP Adresi</Label>
              <Input
                id="ip-address"
                type="text"
                value={newIPAddress}
                onChange={(e) => setNewIPAddress(e.target.value)}
                placeholder="192.168.1.1"
                data-testid="ip-address-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowIPDialog(false);
                setNewIPAddress('');
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleAddIP}
              disabled={isIPLoading || !newIPAddress.trim()}
              data-testid="confirm-add-ip-button"
            >
              {isIPLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
